// Font System Configuration
// This file defines the primary and secondary font system for the application

export interface FontSystem {
  primary: {
    name: string;
    family: string;
    fallback: string;
  };
  secondary: {
    name: string;
    family: string;
    fallback: string;
  };
}

// Default font system configuration
export const defaultFontSystem: FontSystem = {
  primary: {
    name: 'Sen',
    family: "'Sen', sans-serif",
    fallback: 'sans-serif'
  },
  secondary: {
    name: 'Sen',
    family: "'Sen', sans-serif",
    fallback: 'sans-serif'
  }
};

// Font system presets for different publications
export const fontSystemPresets = {
  chicago: {
    primary: {
      name: 'Libre Baskerville',
      family: "'Libre Baskerville', serif",
      fallback: 'serif'
    },
    secondary: {
      name: 'Geist',
      family: "'Geist', sans-serif",
      fallback: 'sans-serif'
    }
  },
  boston: {
    primary: {
      name: 'Libre Baskerville',
      family: "'Libre Baskerville', serif",
      fallback: 'serif'
    },
    secondary: {
      name: 'Geist',
      family: "'Geist', sans-serif",
      fallback: 'sans-serif'
    }
  },
  seattle: {
    primary: {
      name: 'Inter',
      family: "'Inter', sans-serif",
      fallback: 'sans-serif'
    },
    secondary: {
      name: 'Geist',
      family: "'Geist', sans-serif",
      fallback: 'sans-serif'
    }
  },
  miami: {
    primary: {
      name: 'Playfair Display',
      family: "'Playfair Display', serif",
      fallback: 'serif'
    },
    secondary: {
      name: 'Geist',
      family: "'Geist', sans-serif",
      fallback: 'sans-serif'
    }
  }
};

// Utility functions for font system
export function getPrimaryFont(fontSystem: FontSystem = defaultFontSystem): string {
  return fontSystem.primary.family;
}

export function getSecondaryFont(fontSystem: FontSystem = defaultFontSystem): string {
  return fontSystem.secondary.family;
}

export function formatFontFamily(fontFamily: string, fontSystem: FontSystem = defaultFontSystem): string {
  // Handle legacy font names
  if (fontFamily === "Sorts Mill Goudy") {
    return fontSystem.primary.family;
  }
  if (fontFamily === "Geist") {
    return fontSystem.secondary.family;
  }
  if (fontFamily === "Libre Baskerville") {
    return fontSystem.primary.family;
  }
  if (fontFamily === "Inter") {
    return fontSystem.secondary.family;
  }
  if (fontFamily === "Playfair Display") {
    return fontSystem.primary.family;
  }
  
  // Return as-is if it's already formatted
  if (fontFamily.includes("'") || fontFamily.includes('"')) {
    return fontFamily;
  }
  
  // Default fallback
  return fontFamily;
}

// CSS Custom Properties for runtime font changes
export function getFontCSSVariables(fontSystem: FontSystem = defaultFontSystem) {
  return {
    '--font-primary': fontSystem.primary.family,
    '--font-secondary': fontSystem.secondary.family,
    '--font-primary-name': fontSystem.primary.name,
    '--font-secondary-name': fontSystem.secondary.name,
  };
}

// Apply font system to document
export function applyFontSystem(fontSystem: FontSystem = defaultFontSystem) {
  const cssVars = getFontCSSVariables(fontSystem);
  const root = document.documentElement;
  
  Object.entries(cssVars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

// Font usage guidelines
export const fontUsageGuidelines = {
  primary: [
    'Main headings (h1, h2, h3)',
    'Hero section titles',
    'Section titles',
    'Navigation elements',
    'Call-to-action buttons',
    'Brand elements'
  ],
  secondary: [
    'Body text',
    'Statistics and numbers',
    'Form labels',
    'Card content',
    'Data tables',
    'Charts and graphs',
    'Small text elements'
  ]
};
